import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  LogOut, User, Edit, Save, Briefcase, GraduationCap, Camera, FileText, CheckCircle, XCircle, Clock, MapPin, X, Send, ChevronRight, Menu
} from 'lucide-react';
import './CandidateStyles.css';
import CandidateJobApplicationForm from './CandidateJobApplicationForm';

// Helper to display Date
const formatDMY = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return ''; 
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

// Helper to color code status
const getStatusColor = (status) => {
  if (!status) return '#64748b'; // Grey
  if (status.includes('Select') || status.includes('Offer')) return '#16a34a'; // Green
  if (status.includes('Reject')) return '#dc2626'; // Red
  return '#d97706'; // Orange/Yellow for Pending/In-Progress
};

// Helper for backend date format
const parseDMYtoISO = (dmy) => {
  if (!dmy) return null;
  const parts = dmy.split('-');
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  return `${yyyy}-${mm}-${dd}`;
};

// --- HELPER: SMART URL HANDLER ---
const getFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('https')) {
      return path; 
  }
  return `${process.env.REACT_APP_API_BASE_URL}/${path.replace(/\\/g, '/')}`;
};

const CandidateProfile = ({ onLogout }) => {
  const candidateId = localStorage.getItem('candidateId');
  
  // --- CHANGE: Default activeTab set to 'applications' ---
  const [activeTab, setActiveTab] = useState('apply');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [candidateJobs, setCandidateJobs] = useState([]);
  const [jobModalTarget, setJobModalTarget] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [applyingJob, setApplyingJob] = useState(null); // set to open the apply form 
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [message, setMessage] = useState('');

  // 1. Fetch Profile
  useEffect(() => {
    if (!candidateId) return;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/candidate/profile/${candidateId}`)
      .then((res) => {
        const data = res.data;
        setProfile(data);
        
        // Initialize Form
        setForm({
          username: data.username || '',
          fullName: data.fullName || '',
          email: data.email || '',
          phone: data.phone || '',
          role: data.role || '',
          linkedin: data.linkedin || '',
          experience: data.experience || '',
          relevantExperience: data.relevantExperience || '',
          presentOrganization: data.presentOrganization || '',
          currentCTC: data.currentCTC || '',
          expectedCTC: data.expectedCTC || '', 
          currentLocation: data.currentLocation || '',
          location: data.location || '', 
          certification: data.certification || '', 
          skills: Array.isArray(data.skills) ? data.skills.join(', ') : data.skills || '',
          preferredLocation: Array.isArray(data.preferredLocation) ? data.preferredLocation.join(', ') : data.preferredLocation || '',
          servingNotice: !!data.servingNotice,
          lastWorkingDay: data.lastWorkingDay ? formatDMY(data.lastWorkingDay) : '',
        });
        
        if (data.photoPath) {
            setPhotoPreview(getFileUrl(data.photoPath));
        }
      })
      .catch((err) => console.error(err));
  }, [candidateId]);

  // 2. Fetch Applications
  useEffect(() => {
    if (profile?.email) {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/candidate/applications/${profile.email}`)
        .then((res) => setApplications(res.data))
        .catch((err) => console.error("Error fetching applications:", err));
    }
  }, [profile]);

  // 3. Fetch Jobs open for candidates
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/candidate-visible`)
      .then((res) => setCandidateJobs(res.data))
      .catch((err) => console.error("Error fetching candidate jobs:", err));
  }, []);


  const appliedJobIds = new Set(applications.map(a => a.jobId));

  const handleJobCardClick = (job) => {
    setJobModalTarget(job);
    setShowJobModal(true);
  };

  const handleApplicationSubmitted = (newApp) => {
    setApplications(prev => [newApp, ...prev]);
    setApplyingJob(null);
  };

  const handleLogoutClick = () => {
    localStorage.clear();
    if (onLogout) onLogout();
    else window.location.href = '/'; 
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleUpdate = async () => {
    try {
      const formData = new FormData();
      const formCopy = { ...form };
      
      if (typeof formCopy.skills === 'string') {
          formCopy.skills = formCopy.skills.split(',').map(s => s.trim()).filter(Boolean).join(', ');
      }
      if (typeof formCopy.preferredLocation === 'string') {
          formCopy.preferredLocation = formCopy.preferredLocation.split(',').map(s => s.trim()).filter(Boolean).join(', ');
      }

      if (formCopy.servingNotice && formCopy.lastWorkingDay) {
         if (formCopy.lastWorkingDay.includes('-')) {
             const isoDate = parseDMYtoISO(formCopy.lastWorkingDay);
             if (isoDate) formCopy.lastWorkingDay = isoDate;
         }
      } else {
         formCopy.lastWorkingDay = '';
      }

      Object.keys(formCopy).forEach((key) => {
        if (['_id', 'candidateId', '__v', 'resumePath', 'photoPath', 'password'].includes(key)) return;
        const val = formCopy[key];
        if (val !== null && val !== undefined) {
           formData.append(key, val);
        }
      });

      if (resumeFile) formData.append('resume', resumeFile);
      if (photoFile) formData.append('photo', photoFile);

      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/candidate/profile/${candidateId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setProfile(res.data.candidate);
      if (res.data.candidate.photoPath) {
          setPhotoPreview(getFileUrl(res.data.candidate.photoPath));
      }

      setEditing(false);
      setResumeFile(null);
      setPhotoFile(null);
      setMessage('Profile updated successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error("Update failed:", err);
      setMessage('Update failed. Check console.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'servingNotice') setForm({ ...form, servingNotice: value === 'true' });
    else setForm({ ...form, [name]: value });
  };

  if (!profile) return <div className="c-loading-screen">Loading Profile...</div>;

  return (
    <div className="candidate-dashboard-container">
      {/* Sidebar */}
      <aside className={`c-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button className="c-sidebar-close" onClick={() => setSidebarOpen(false)}>
          <X size={18} />
        </button>
        <div className="c-sidebar-header">
          <div className="c-logo-icon"><GraduationCap size={28} color="#ea580c"/></div>
          <h2>Candidate<span style={{color:'#ea580c'}}>Portal</span></h2>
        </div>
        
        <nav className="c-sidebar-nav">
  <button className={`c-nav-item ${activeTab === 'apply' ? 'active' : ''}`} onClick={() => { setActiveTab('apply'); setSidebarOpen(false); }}>
    <FileText size={20} /><span>Job Board</span>
  </button>
  <button className={`c-nav-item ${activeTab === 'applications' ? 'active' : ''}`} onClick={() => { setActiveTab('applications'); setSidebarOpen(false); }}>
    <Briefcase size={20} /><span>My Applications</span>
  </button>
  
  <button className={`c-nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => { setActiveTab('profile'); setSidebarOpen(false); }}>
    <User size={20} /><span>My Profile</span>
  </button>
</nav>

        <div className="c-sidebar-footer">
          <button className="c-nav-item c-logout-btn" onClick={handleLogoutClick}>
            <LogOut size={20} /><span>Log Out</span>
          </button>
        </div>
      </aside>
      {sidebarOpen && <div className="c-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="c-main-content">
        <header className="c-top-bar">
          <button className="c-hamburger-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
          </button>
          <div className="c-top-bar-title">
            <h1>
              {activeTab === 'profile' ? 'My Profile' : activeTab === 'apply' ? 'Apply for a Job' : 'Application Status'}
            </h1>
            <p>Welcome back, {profile.username}</p>
          </div>
          <div className="c-user-pill">
            <div className="c-avatar">{profile.username?.charAt(0).toUpperCase()}</div>
            <span className="c-user-name">{profile.username}</span>
          </div>
        </header>

        <div className="c-content-wrapper">
          
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="c-card c-profile-card">
                <div className="c-section-header">
                  <h3>Personal & Professional Details</h3>
                  {!editing && (
                    <button className="c-btn-secondary" onClick={() => setEditing(true)}>
                      <Edit size={16} /> Edit Profile
                    </button>
                  )}
                </div>

                {message && <div className={`c-alert ${message.includes('failed') ? 'c-alert-error' : 'c-alert-success'}`}>{message}</div>}

                {editing ? (
                // --- EDIT MODE ---
                <div className="c-edit-form">
                    <div className="c-photo-upload-section">
                        <div className="c-photo-preview-large">
                            {photoPreview ? <img src={photoPreview} alt="Profile" /> : <User size={40} color="#cbd5e1"/>}
                        </div>
                        <label htmlFor="photo-upload" className="c-upload-label">
                            <Camera size={16} /> Change Photo
                        </label>
                        <input id="photo-upload" type="file" accept="image/*" onChange={handlePhotoChange} hidden />
                    </div>

                    <div className="c-form-grid-2">
                        <div className="c-input-group"><label>Username</label><input value={form.username} disabled className="c-input-disabled" /></div>
                        <div className="c-input-group"><label>Full Name</label><input name="fullName" value={form.fullName} onChange={handleChange} /></div>
                        <div className="c-input-group"><label>Email</label><input value={form.email} disabled className="c-input-disabled" /></div>
                        <div className="c-input-group"><label>Phone Number</label><input name="phone" value={form.phone} onChange={handleChange} /></div>
                        <div className="c-input-group"><label>Current Role</label><input name="role" value={form.role} onChange={handleChange} /></div>
                        <div className="c-input-group"><label>LinkedIn URL</label><input name="linkedin" value={form.linkedin} onChange={handleChange} /></div>
                    </div>

                    <div className="c-form-grid-2">
                        <div className="c-input-group"><label>Total Experience</label><input name="experience" value={form.experience} onChange={handleChange} placeholder="e.g. 5 Years" /></div>
                        <div className="c-input-group"><label>Relevant Experience</label><input name="relevantExperience" value={form.relevantExperience} onChange={handleChange} placeholder="e.g. 3 Years" /></div>
                        <div className="c-input-group"><label>Current CTC</label><input name="currentCTC" value={form.currentCTC} onChange={handleChange} placeholder="e.g. 10 LPA" /></div>
                        <div className="c-input-group"><label>Expected CTC</label><input name="expectedCTC" value={form.expectedCTC} onChange={handleChange} placeholder="e.g. 15 LPA" /></div>
                    </div>

                    <div className="c-form-grid-2">
                        <div className="c-input-group"><label>Current Location</label><input name="currentLocation" value={form.currentLocation} onChange={handleChange} /></div>
                        <div className="c-input-group"><label>Location (General)</label><input name="location" value={form.location} onChange={handleChange} /></div>
                    </div>

                    <div className="c-input-group"><label>Present Organization</label><input name="presentOrganization" value={form.presentOrganization} onChange={handleChange} /></div>
                    <div className="c-input-group"><label>Certifications (comma separated)</label><input name="certification" value={form.certification} onChange={handleChange} /></div>
                    <div className="c-input-group"><label>Skills (comma separated)</label><input name="skills" value={form.skills} onChange={handleChange} /></div>
                    <div className="c-input-group"><label>Preferred Locations</label><input name="preferredLocation" value={form.preferredLocation} onChange={handleChange} /></div>

                    <div className="c-form-grid-2">
                        <div className="c-input-group">
                        <label>Serving Notice?</label>
                        <select name="servingNotice" value={form.servingNotice ? 'true' : 'false'} onChange={handleChange}>
                            <option value="false">No</option>
                            <option value="true">Yes</option>
                        </select>
                        </div>
                        {form.servingNotice && (
                        <div className="c-input-group">
                            <label>Last Working Day (DD-MM-YYYY)</label>
                            <input name="lastWorkingDay" value={form.lastWorkingDay} onChange={handleChange} />
                        </div>
                        )}
                    </div>

                    <div className="c-input-group c-file-group">
                        <label>Upload New Resume (PDF/DOC)</label>
                        <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResumeFile(e.target.files[0])} />
                    </div>

                    <div className="c-form-actions">
                        <button className="c-btn-primary" onClick={handleUpdate}><Save size={18}/> Save Changes</button>
                        <button className="c-btn-text" onClick={() => setEditing(false)}>Cancel</button>
                    </div>
                </div>
                ) : (
                // --- VIEW MODE ---
                <div className="c-view-details">
                    <div className="c-profile-header-row">
                        <div className="c-profile-pic-wrapper">
                            {photoPreview ? <img src={photoPreview} alt="Profile" /> : <User size={40} color="#94a3b8"/>}
                        </div>
                        <div className="c-profile-info-header">
                            <h2>{profile.fullName || profile.username}</h2>
                            <p>{profile.role || 'Role not updated'}</p>
                            <div className="c-header-badges">
                                {profile.currentLocation && <span className="c-badge-gray"><MapPin size={12}/> {profile.currentLocation}</span>}
                                {profile.experience && <span className="c-badge-gray"><Briefcase size={12}/> {profile.experience}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="c-details-grid">
                        <div className="c-detail-item">
                            <label>Email</label>
                            <span>{profile.email}</span>
                        </div>
                        <div className="c-detail-item">
                            <label>Phone</label>
                            <span>{profile.phone}</span>
                        </div>
                        <div className="c-detail-item">
                            <label>LinkedIn</label>
                            <span>{profile.linkedin ? <a href={profile.linkedin} target="_blank" rel="noreferrer" className="c-link">View Profile</a> : 'Not added'}</span>
                        </div>
                        <div className="c-detail-item">
                            <label>Preferred Locations</label>
                            <span>{Array.isArray(profile.preferredLocation) ? profile.preferredLocation.join(', ') : profile.preferredLocation || '--'}</span>
                        </div>
                        <div className="c-detail-item">
                            <label>Skills</label>
                            <span>{Array.isArray(profile.skills) ? profile.skills.join(', ') : profile.skills || '--'}</span>
                        </div>
                        <div className="c-detail-item">
                            <label>Certifications</label>
                            <span>{profile.certification || 'None listed'}</span>
                        </div>
                        <div className="c-detail-item">
                            <label>Current CTC</label>
                            <span>{profile.currentCTC || '--'}</span>
                        </div>
                        <div className="c-detail-item">
                            <label>Expected CTC</label>
                            <span>{profile.expectedCTC || '--'}</span>
                        </div>
                         <div className="c-detail-item">
                            <label>Notice Period</label>
                            <span>{profile.servingNotice ? `Yes (LWD: ${formatDMY(profile.lastWorkingDay)})` : 'No'}</span>
                        </div>
                    </div>
                    
                    {profile.resumePath && (
                        <div className="c-resume-section">
                        <a href={getFileUrl(profile.resumePath)} target="_blank" rel="noreferrer" className="c-btn-primary">
                            <FileText size={18}/> View Resume
                        </a>
                        </div>
                    )}
                </div>
                )}
            </div>
          )}

          {/* APPLICATIONS TAB */}
          {activeTab === 'applications' && (
            <div className="c-card">
              <div className="c-section-header">
                <h3>My Job Applications</h3>
              </div>
              
              <div className="c-applications-container">
                {applications.length === 0 ? (
                  <div className="c-empty-state">
                    <FileText size={48} />
                    <p>No applications found.</p>
                  </div>
                ) : (
                  <table className="c-table">
                    <thead>
                      <tr>
                        <th>Job Role</th>
                        <th>Company</th>
                        <th>Applied Date</th>
                        <th>Current Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id}>
                          <td className="c-fw-bold">{app.job?.role || 'Unknown Role'}</td>
                          <td>{app.job?.companyName || 'Unknown Company'}</td>
                          <td className="c-text-muted">{formatDMY(app.submittedAt)}</td>
                          <td>
                            <span 
                                className="c-status-badge" 
                                style={{
                                    backgroundColor: getStatusColor(app.candidateStatus) + '20', 
                                    color: getStatusColor(app.candidateStatus)
                                }}
                            >
                              {app.candidateStatus === 'Pending Review' ? <Clock size={14}/> : 
                               app.candidateStatus && (app.candidateStatus.includes('Select') || app.candidateStatus.includes('Offer')) ? <CheckCircle size={14}/> : <XCircle size={14}/>}
                              {app.candidateStatus || 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

                {/* APPLY TAB */}
      {activeTab === 'apply' && !applyingJob && (
        <div className="c-card">
          <div className="c-section-header">
            <h3>Open Positions</h3>
          </div>
          <div className="c-jobs-grid">
            {candidateJobs.length > 0 ? candidateJobs.map((job) => (
              <div key={job._id} className="c-job-card" onClick={() => handleJobCardClick(job)} style={{ cursor: 'pointer' }}>
                <div className="c-job-header">
                  <div className="c-company-icon">{job.companyName?.[0] || 'C'}</div>
                  <div className="c-job-info">
                    <h3>{job.role}</h3>
                    <p>{job.companyName}</p>
                  </div>
                  <ChevronRight className="c-arrow-icon" size={18} />
                </div>
                <div className="c-job-divider" />
                <div className="c-job-tags">
                  <span><MapPin size={12} /> {job.location}</span>
                  <span>{job.ctc}</span>
                  <span><Clock size={12} /> {formatDMY(job.applyByDate)}</span>
                </div>
                {appliedJobIds.has(job._id) && (
                  <div style={{ marginTop: '10px', fontSize: '0.75rem', fontWeight: '700', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle size={12} /> Applied
                  </div>
                )}
              </div>
            )) : (
              <div className="c-empty-state">
                <FileText size={48} />
                <p>No open jobs right now.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'apply' && applyingJob && (
        <CandidateJobApplicationForm
          job={applyingJob}
          candidateId={candidateId}
          profile={profile}
          onBack={() => setApplyingJob(null)}
          onSubmitted={handleApplicationSubmitted}
        />
      )}

      {/* JOB DETAILS MODAL (Apply Now / Applied) */}
      {showJobModal && jobModalTarget && (
        <div className="modal-overlay" onClick={() => setShowJobModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{jobModalTarget.role}</h2>
                <p>{jobModalTarget.companyName} · {jobModalTarget.location} · {jobModalTarget.typeOfWork}</p>
              </div>
              <button className="close-btn" onClick={() => setShowJobModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <div className="info-grid-3">
                  <div className="info-item"><label>CTC / Stipend</label><span style={{ color: '#059669' }}>{jobModalTarget.ctc}</span></div>
                  <div className="info-item"><label>Apply By</label><span style={{ color: '#dc2626' }}>{formatDMY(jobModalTarget.applyByDate)}</span></div>
                </div>
              </div>
              {jobModalTarget.aboutJob && (
                <div className="detail-section">
                  <h4>About the Job</h4>
                  <div className="text-block">{jobModalTarget.aboutJob}</div>
                </div>
              )}
              {jobModalTarget.aboutCompany && (
                <div className="detail-section">
                  <h4>About Company</h4>
                  <div className="text-block">{jobModalTarget.aboutCompany}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {appliedJobIds.has(jobModalTarget._id) ? (
                <button className="r-modal-submit-btn" disabled style={{ background: '#94a3b8', cursor: 'default' }}>
                  <CheckCircle size={15} /> Applied
                </button>
              ) : (
                <button
                  className="r-modal-submit-btn"
                  onClick={() => { setApplyingJob(jobModalTarget); setShowJobModal(false); }}
                >
                  <Send size={15} /> Apply Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}

        </div>
      </main>
    </div>
  );
};

export default CandidateProfile;